import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"
import * as members from "../members"

// QC sign-off capabilities attached to a member's profile (F-A3; SPEC-021 B3). They are
// independent of the planning role (member/planner/view_only): a person can be "Member + QA"
// or "Planner + Reviewer".
//
// SPEC-021 cutover: capabilities are READ off the member dump (`member.qcCapabilities` — the
// generic members endpoints serialize them read-only) and CHANGED only via the e-signed
// `POST /lims/qc-capabilities` (hyphenated) with `{input:{memberId, capabilities, password,
// reason}}`. There is NO GET and no separate redux slice state any more — this module is
// helpers + the signed write thunk.
//
// Authorisation (SPEC-007 D2 rev. 2026-06-11, enforced server-side and mirrored here for the
// UI): a member with the PLANNER planning role administers QC capabilities for their team; a
// holder of the "qa-manager" capability may also (delegated administration). Anyone else
// cannot. (The earlier qa-manager-only rule with an ADMIN bootstrap was dropped: ADMIN-role
// members have no team-setup UI, so the first qa-manager could never be granted in-app.)
// Ordered to mirror the QC pipeline (analyst → reviewer → supervisor → qa → qa-manager) so the
// MemberConfig checkboxes read in flow order. `supervisor` (SPEC-032 / F0) is the chemist role
// that owns lab analysis-review and stamdata change-control; the result flow does not consume it
// yet (that is F3). The backend QcCapabilitiesUpdate validator accepts the same set.
export const QC_CAPABILITIES = ["analyst", "reviewer", "supervisor", "qa", "qa-manager"]

// The head of the quality column (SPEC-057: appointed by the CUSTOMER_ADMIN, never via the
// team-facing capability route). A qa-manager holder administers the `qa` capability and the
// QA staff lifecycle on their own LIMS team.
export const QA_MANAGER = "qa-manager"
export const QA = "qa"

// SPEC-057 L1 — the drift column (PLANNER) attests the lab qualifications. `qa-manager` is never
// assignable here (customer-admin route only).
//
// SPEC-061 (#4153) removed the quality column's half: `qa` is no longer assignable OR revocable
// on an existing member, by anyone. It is set when a QA member is created (LIMS > QA team) and
// ends when they are archived — so there is no allow-set left for a qa-manager here, and the
// actor's own qa-manager status and self-ness no longer affect the answer. Being able to GRANT
// `qa` was a backdoor to archiving an arbitrary colleague.
const PLANNER_ASSIGNABLE = ["analyst", "reviewer", "supervisor"]

// The capabilities the CURRENT actor may change on the given target — the FE mirror of the
// backend's diff-based matrix (`_authorize_capability_diff`); the server remains the law.
// The matrix is ABSOLUTE: like the backend (and like archiveDecision below) it never consults
// gmpControlsRelaxed. SPEC-010's dev relaxation is about SIGNING a record you would otherwise be
// excluded from, never about WHO MAY APPOINT WHOM — and offering capabilities here that the
// server then refuses with a 403 just moves the failure to the e-signature step.
export function assignableCapabilities({ isPlanner }) {
  const allowed = new Set()
  if (isPlanner) {
    PLANNER_ASSIGNABLE.forEach((capability) => allowed.add(capability))
  }
  return QC_CAPABILITIES.filter((capability) => allowed.has(capability))
}

// #4154 rev. — "QA-only staff": a member who exists PURELY for the quality column. They hold
// qa/qa-manager, carry the MEMBER planning role, and hold none of the drift-column lab
// qualifications, so nothing about them is the planner's business. Such a member is hidden from
// Setup > Team entirely and the server refuses planner edits on them (backend
// `_authorize_member_edit`) — the list filter is the mirror, not the rule.
//
// A QA holder who is a PLANNER, or who also holds analyst/reviewer/supervisor, is NOT QA-only:
// they take part in the ordinary Plannertech workflow, so they stay visible and the planner keeps
// the lab qualifications they are responsible for attesting (SPEC-057 L1). Granting `qa` itself
// has been impossible since SPEC-061 either way.
const DRIFT_CAPABILITIES = ["analyst", "reviewer", "supervisor"]

export function isQaOnlyStaff({ showLims, member }) {
  if (!showLims || !member) {
    return false
  }
  const capabilities = new Set(member.qcCapabilities || [])
  const holdsQa = capabilities.has(QA) || capabilities.has(QA_MANAGER)
  const holdsDrift = DRIFT_CAPABILITIES.some((capability) => capabilities.has(capability))
  return holdsQa && !holdsDrift && String(member.role || "").toUpperCase() === "MEMBER"
}

// Whether the CURRENT user holds qa-manager ON A LIMS TEAM — the FE mirror of the backend's
// `_is_lims_qa_manager` (SPEC-044: on a Standard team a stale capability string grants nothing).
export function selectIsLimsQaManager(state) {
  return !!(state.teamStatus && state.teamStatus.showLims) && hasQcCapability(state, state.auth.memberId, QA_MANAGER)
}

// SPEC-057 L1 — the FE mirror of the backend's archive authorisation matrix
// (`_authorize_archive`). Returns `{ allowed, tooltipKey }`:
//   allowed=true             → show an active archive action.
//   tooltipKey set           → show a DISABLED archive action with the translated tooltip
//                              (the backend answers 409 MEMBER_HOLDS_QA_ROLE / 403 as fallback).
//   neither                  → hide the action (the actor has no archive power over the target).
// The matrix is ABSOLUTE: like the backend it never consults gmpControlsRelaxed.
export function archiveDecision({ actorRole, actorIsQaManager, actorMemberId, showLims, target }) {
  const targetCapabilities = new Set(target.qcCapabilities || [])
  const targetHoldsQa = showLims && (targetCapabilities.has(QA) || targetCapabilities.has(QA_MANAGER))
  const targetHoldsQaManager = showLims && targetCapabilities.has(QA_MANAGER)

  if (!targetHoldsQa) {
    // Standard target: archiving is unchanged for the roles that could already reach it. A
    // MEMBER-role actor (i.e. a qa-manager holder, who only got here via SPEC-057) has no
    // archive power outside the quality column — hide the action.
    if (actorRole === "member") {
      return { allowed: false, tooltipKey: null }
    }
    return { allowed: true, tooltipKey: null }
  }

  // The target holds qa/qa-manager on a LIMS team: only the quality column may act here.
  if (actorIsQaManager && !targetHoldsQaManager && target.id !== actorMemberId) {
    return { allowed: true, tooltipKey: null }
  }
  if (targetHoldsQaManager) {
    return { allowed: false, tooltipKey: "limsArchiveQaManagerTooltip" }
  }
  return { allowed: false, tooltipKey: "limsArchiveQaTooltip" }
}

// Set a member's QC capabilities behind an electronic signature. `signature` is the
// ESignatureModal payload; its password (+ reason) ride at the TOP LEVEL of the `input`
// envelope per the backend schema (QcCapabilitiesUpdate). On success the members store is
// refreshed so `member.qcCapabilities` reflects the change everywhere.
export const setForMember = asyncAction(
  "limsCapabilities/set",
  (dispatch, memberId, capabilities, signature = null) => {
    return limsRequest("POST", "/lims/qc-capabilities", {
      input: {
        memberId,
        capabilities: capabilities || [],
        password: (signature && signature.password) || undefined,
        reason: (signature && signature.reason) || null,
      },
    }).then((res) => {
      // Re-read the members so the updated qcCapabilities land in the store (the capability
      // checks below read straight off the member records).
      dispatch(members.fetch())
      return res.item
    })
  }
)

// The QC capabilities currently held by a member (read off the member dump).
export function capabilitiesForMember(state, memberId) {
  const member = memberId && state.members && state.members[memberId]
  return (member && member.qcCapabilities) || []
}

// Whether the given member currently has the given QC capability.
export function hasQcCapability(state, memberId, capability) {
  return capabilitiesForMember(state, memberId).includes(capability)
}

// May the CURRENT user administer QC capabilities? Mirrors the server (SPEC-007 D2 rev.):
// a PLANNER (team lead), or a holder of the "qa-manager" capability (delegated). Returns a
// boolean.
export function selectCanAdministerCapabilities(state) {
  if (state.auth.role === "planner") {
    return true
  }
  return hasQcCapability(state, state.auth.memberId, QA_MANAGER)
}
